import type { MuscleSlug } from '@/api/types/workout'
import { cn } from '@/lib/utils'

import { isMuscleSlug, muscleLabel } from '../../muscle-level'
import {
  BODY_BACK_OUTLINE,
  BODY_BACK_PARTS,
  BODY_BACK_VIEWBOX,
  BODY_FRONT_OUTLINE,
  BODY_FRONT_PARTS,
  BODY_FRONT_VIEWBOX,
  type BodyPartPaths,
} from './body-paths'

type Props = {
  /** 筋群ごとの塗り色。無い筋群は地の色 */
  colors: ReadonlyMap<MuscleSlug, string>
  /** 渡すと筋群をタップできる */
  onMuscleClick?: (slug: MuscleSlug) => void
  className?: string
}

type FigureProps = Props & {
  label: string
  viewBox: string
  outline: string
  parts: BodyPartPaths[]
}

const BodyFigure = ({ colors, onMuscleClick, label, viewBox, outline, parts }: FigureProps) => (
  <figure className="flex flex-1 flex-col items-center gap-1">
    <svg viewBox={viewBox} role="img" aria-label={`人体図（${label}）`} className="min-h-0 w-full flex-1">
      {parts.map((part) => {
        const slug = isMuscleSlug(part.slug) ? part.slug : null
        const fill = slug ? colors.get(slug) : undefined
        const clickable = slug !== null && onMuscleClick !== undefined
        return (
          <g
            key={part.slug}
            className={cn(!fill && 'fill-muted-foreground/25', clickable && 'cursor-pointer')}
            style={fill ? { fill } : undefined}
            onClick={clickable ? () => onMuscleClick(slug) : undefined}
          >
            {slug && <title>{muscleLabel(slug)}</title>}
            {part.paths.map((d, i) => (
              <path key={i} d={d} />
            ))}
          </g>
        )
      })}
      <path
        d={outline}
        fill="none"
        strokeWidth={2}
        className="stroke-muted-foreground/40"
        style={{ vectorEffect: 'non-scaling-stroke' }}
      />
    </svg>
    <figcaption className="text-[10px] text-muted-foreground">{label}</figcaption>
  </figure>
)

/** 前面と背面の人体図を並べ、筋群に色を付ける */
export const MuscleBodyMap = ({ className, ...props }: Props) => (
  <div className={cn('flex gap-2', className)}>
    <BodyFigure
      {...props}
      label="前"
      viewBox={BODY_FRONT_VIEWBOX}
      outline={BODY_FRONT_OUTLINE}
      parts={BODY_FRONT_PARTS}
    />
    <BodyFigure {...props} label="後" viewBox={BODY_BACK_VIEWBOX} outline={BODY_BACK_OUTLINE} parts={BODY_BACK_PARTS} />
  </div>
)
